import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatBytes, type User } from './api';
import { Admin } from './Admin';
import { Explorer } from './Explorer';
import { Logo, useMarca } from './marca';
import { Papelera } from './Papelera';
import { Perfil } from './Perfil';
import { CompartidoConmigo, CompartidoPorMi, LinksPublicos } from './Vistas';
import { aplicarTema, parseTema } from './tema';

type Vista = 'archivos' | 'conmigo' | 'pormi' | 'links' | 'papelera' | 'perfil' | 'admin';

const VISTAS: Array<{ id: Vista; etiqueta: string; soloAdmin?: boolean; ocultaParaUser?: boolean }> = [
  { id: 'archivos', etiqueta: 'Mis archivos', ocultaParaUser: true },
  { id: 'conmigo', etiqueta: 'Compartido conmigo' },
  { id: 'pormi', etiqueta: 'Compartido por mí', ocultaParaUser: true },
  { id: 'links', etiqueta: 'Links públicos', ocultaParaUser: true },
  { id: 'papelera', etiqueta: 'Papelera', ocultaParaUser: true },
  { id: 'admin', etiqueta: 'Administración', soloAdmin: true },
  { id: 'perfil', etiqueta: 'Mi cuenta' },
];

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api
      .me()
      .then((data) => {
        setUser(data.user);
        const { modo, paleta } = parseTema(data.user.theme);
        aplicarTema(modo, paleta);
      })
      .catch(() => setUser(null))
      .finally(() => setCargando(false));
  }, []);

  // Con el tema en "sistema", seguir el cambio del sistema operativo en vivo.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const alCambiar = () => {
      const { modo, paleta } = parseTema(user?.theme);
      if (modo === 'sistema') aplicarTema(modo, paleta);
    };
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, [user?.theme]);

  if (cargando) return <main className="centro">Cargando…</main>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <Shell
      user={user}
      onUser={setUser}
      onLogout={() => {
        void api.logout();
        setUser(null);
      }}
    />
  );
}

function Shell({
  user,
  onUser,
  onLogout,
}: {
  user: User;
  onUser: (u: User) => void;
  onLogout: () => void;
}) {
  const esUser = user.role === 'user';
  const marca = useMarca();
  const [vista, setVista] = useState<Vista>(esUser ? 'conmigo' : 'archivos');
  const [path, setPath] = useState('');
  const [quota, setQuota] = useState({ usedBytes: '0', totalBytes: user.quotaBytes });
  const [menuAbierto, setMenuAbierto] = useState(false);

  const onQuota = useCallback((q: { usedBytes: string; totalBytes: string }) => setQuota(q), []);

  const visibles = VISTAS.filter(
    (v) => !(v.soloAdmin && user.role !== 'admin') && !(v.ocultaParaUser && esUser),
  );

  const usado = Number(quota.usedBytes);
  const total = Number(quota.totalBytes) || 1;
  const porcentaje = Math.min(100, Math.round((usado / total) * 100));

  return (
    <div className="app">
      <header className="barra">
        <div className="barra-izquierda">
          <button
            className="hamburguesa"
            onClick={() => setMenuAbierto((v) => !v)}
            aria-expanded={menuAbierto}
            aria-label="Menú"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 4h12v1.6H2V4Zm0 3.2h12v1.6H2V7.2Zm0 3.2h12V12H2v-1.6Z" />
            </svg>
          </button>

          <Logo className="logo" />
          <span className="producto">{marca.producto}</span>
        </div>

        <div className="barra-derecha">
          {!esUser && (
            <span
              className="cuota"
              title={`${formatBytes(quota.usedBytes)} de ${formatBytes(quota.totalBytes)}`}
            >
              <span className="cuota-barra">
                <span className="cuota-relleno" style={{ width: `${porcentaje}%` }} />
              </span>
              <span className="cuota-texto">
                {formatBytes(quota.usedBytes)} / {formatBytes(quota.totalBytes)}
              </span>
            </span>
          )}
          <span className="usuario">{user.displayName}</span>
          <button className="link" onClick={onLogout}>
            Salir
          </button>
        </div>
      </header>

      <nav className={menuAbierto ? 'pestanias abierto' : 'pestanias'}>
        {visibles.map((v) => (
          <button
            key={v.id}
            className={vista === v.id ? 'pestania activa' : 'pestania'}
            onClick={() => {
              setVista(v.id);
              setMenuAbierto(false);
            }}
          >
            {v.etiqueta}
          </button>
        ))}
      </nav>

      <main className="contenido">
        {vista === 'archivos' && <Explorer path={path} onPathChange={setPath} onQuota={onQuota} />}
        {vista === 'conmigo' && <CompartidoConmigo />}
        {vista === 'pormi' && <CompartidoPorMi />}
        {vista === 'links' && <LinksPublicos />}
        {vista === 'papelera' && <Papelera onQuota={onQuota} />}
        {vista === 'perfil' && <Perfil user={user} onUser={onUser} />}
        {vista === 'admin' && user.role === 'admin' && <Admin usuarioActual={user.id} />}
      </main>
    </div>
  );
}

function Login({ onLogin }: { onLogin: (user: User) => void }) {
  const marca = useMarca();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setEnviando(true);
    setError(null);

    try {
      const { user } = await api.login(username, password);
      const { modo, paleta } = parseTema(user.theme);
      aplicarTema(modo, paleta);
      onLogin(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos iniciar sesión.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="centro login-fondo">
      <form className="tarjeta-login" onSubmit={submit}>
        <Logo className="logo-login" />
        <h1>{marca.producto}</h1>
        <p className="subtitulo-login">{marca.lema}</p>

        <label htmlFor="usuario">Usuario</label>
        <input
          id="usuario"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />

        <label htmlFor="password">Contraseña</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" disabled={enviando}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </main>
  );
}
